import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Character } from "@/mocks/characters";
import { supabase, rowToCharacter, CharacterRow } from "@/lib/supabase";

const GPS_STATUS_KEY = "charamap_gps_granted";
const GPS_AREA_KEY = "charamap_gps_area";
const GPS_LOCATION_KEY = "charamap_gps_location";
const GPS_DISPLAYED_IDS_KEY = "charamap_displayed_ids";
// バージョンを上げると古いキャッシュを強制クリア
const GPS_CACHE_VERSION = "v3";
const GPS_CACHE_VERSION_KEY = "charamap_cache_ver";

// ~500m at mid-latitudes — minor movement threshold
const LOCATION_CHANGE_THRESHOLD = 0.005;
// ~3km — major movement threshold (reshuffle characters)
const LOCATION_MAJOR_THRESHOLD = 0.03;

const DEFAULT_ZOOM = 15;
const MAX_DISPLAYED = 13;

// Spawn radius tiers (in degrees) — closest first
const SPAWN_TIERS = [0.002, 0.004, 0.007, 0.011, 0.016, 0.02, 0.025];

// 30分単位のシードを返す（0〜47の整数、1日48スロット）
function get30MinSlot(): number {
  const now = new Date();
  return now.getHours() * 2 + Math.floor(now.getMinutes() / 30);
}

// シードベースの疑似乱数（0〜1）
function seededRandom(seed: number): number {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
}

// シードベースでarrayをシャッフル
function seededShuffle<T>(arr: T[], seed: number): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(seed + i) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Pick MAX_DISPLAYED characters: 1 seeded featured + rest seeded from others
// 30分単位のシードを使うのでページ遷移しても変わらない
function pickDisplayedChars(
  chars: Character[],
  featuredIds: string[],
  previousIds: string[] | null,
  majorMove: boolean
): Character[] {
  const active = chars.filter((c) => c.isActive);
  if (active.length === 0) return [];

  const slot = get30MinSlot();
  const seed = slot * 137;

  // Pick one seeded featured (30分固定)
  const featuredPool = active.filter((c) => featuredIds.includes(c.id));
  const nonFeatured = active.filter((c) => !featuredIds.includes(c.id));

  let chosenFeatured: Character | null = null;
  if (featuredPool.length > 0) {
    const featuredIdx = Math.floor(seededRandom(seed + 9999) * featuredPool.length);
    chosenFeatured = featuredPool[featuredIdx];
  }

  // If major move or no previous selection, pick fresh seeded set
  if (majorMove || !previousIds || previousIds.length === 0) {
    const shuffled = seededShuffle(nonFeatured, seed);
    const slots = MAX_DISPLAYED - (chosenFeatured ? 1 : 0);
    const picked = shuffled.slice(0, slots);
    return chosenFeatured ? [chosenFeatured, ...picked] : picked;
  }

  // Minor move: keep most of the previous selection, just swap featured
  const prevSet = new Set(previousIds);
  const keepNonFeatured = nonFeatured.filter((c) => prevSet.has(c.id));
  const newPool = seededShuffle(nonFeatured.filter((c) => !prevSet.has(c.id)), seed);
  const slots = MAX_DISPLAYED - (chosenFeatured ? 1 : 0);

  // Keep existing ones, fill remaining slots with new ones
  const combined = [...keepNonFeatured, ...newPool].slice(0, slots);
  return chosenFeatured ? [chosenFeatured, ...combined] : combined;
}

function assignPositions(
  chars: Character[],
  center: { lat: number; lng: number },
  featuredId: string
): Record<string, { lat: number; lng: number }> {
  const positions: Record<string, { lat: number; lng: number }> = {};

  const sorted = featuredId
    ? [
        ...chars.filter((c) => c.id === featuredId),
        ...chars.filter((c) => c.id !== featuredId),
      ]
    : chars;

  sorted.forEach((char, idx) => {
    const isFeatured = char.id === featuredId;
    if (isFeatured) {
      const angle = Math.random() * 2 * Math.PI;
      const r = 0.0008 + Math.random() * 0.0005;
      positions[char.id] = {
        lat: center.lat + r * Math.cos(angle),
        lng: center.lng + r * Math.sin(angle),
      };
    } else {
      const tierIdx = Math.min(idx, SPAWN_TIERS.length - 1);
      const radius = SPAWN_TIERS[tierIdx];
      const angle = (idx / sorted.length) * 2 * Math.PI + Math.random() * 0.5;
      const r = radius * (0.5 + Math.random() * 0.5);
      positions[char.id] = {
        lat: center.lat + r * Math.cos(angle),
        lng: center.lng + r * Math.sin(angle),
      };
    }
  });

  return positions;
}

// Remove block-level address parts (丁目, 番地, 号, chome numbers, etc.)
function stripBlockAddress(name: string): string {
  return name
    // Remove kanji chome: 二丁目, 三丁目, etc.
    .replace(/[一二三四五六七八九十百千]+丁目/g, "")
    // Remove numeric chome: 1丁目, 2丁目, etc.
    .replace(/[0-9０-９]+丁目/g, "")
    // Remove banchi: 3番地, 3番
    .replace(/[0-9０-９]+番地?/g, "")
    // Remove go: 5号
    .replace(/[0-9０-９]+号/g, "")
    // Remove trailing/leading hyphens and numbers (e.g. "上野台-2-3")
    .replace(/[-－][0-9０-９]+/g, "")
    // Remove standalone numbers
    .replace(/^[0-9０-９]+$/, "")
    .replace(/\s+/g, "")
    .trim();
}

// Check if a string is purely a block-level address (no meaningful area name)
function isBlockLevelOnly(name: string): boolean {
  const cleaned = stripBlockAddress(name);
  return cleaned.length === 0;
}

async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=ja`,
      { headers: { "Accept-Language": "ja" } }
    );
    const data = await res.json();
    const addr = data.address ?? {};

    // Priority order: broader area names first, avoid road/block-level fields
    // We intentionally skip: road, house_number, postcode
    const candidates = [
      addr.suburb,
      addr.quarter,
      addr.neighbourhood,
      addr.city_district,
      addr.town,
      addr.village,
      addr.municipality,
      addr.city,
      addr.county,
      addr.state_district,
      addr.state,
    ].filter(Boolean) as string[];

    for (const candidate of candidates) {
      // Skip if it's purely block-level (e.g. "二丁目" alone)
      if (isBlockLevelOnly(candidate)) continue;
      const cleaned = stripBlockAddress(candidate);
      if (cleaned.length >= 2) return cleaned;
    }

    // Last resort: try display_name first segment
    if (data.display_name) {
      const parts = (data.display_name as string).split(",");
      for (const part of parts) {
        const trimmed = part.trim();
        if (isBlockLevelOnly(trimmed)) continue;
        const cleaned = stripBlockAddress(trimmed);
        // Only use if it looks like a place name (not a number or postcode)
        if (cleaned.length >= 2 && !/^[0-9０-９\-－]+$/.test(cleaned)) {
          return cleaned;
        }
      }
    }

    return "現在地周辺";
  } catch {
    return "現在地周辺";
  }
}



const MapPage = () => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [displayedChars, setDisplayedChars] = useState<Character[]>([]);
  const [positions, setPositions] = useState<Record<string, { lat: number; lng: number }>>({});
  const [selectedChar, setSelectedChar] = useState<Character | null>(null);
  const [activeTab, setActiveTab] = useState<"distance" | "age">("distance");
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [gpsStatus, setGpsStatus] = useState<"loading" | "granted" | "denied" | "idle">("idle");
  const [nearbyArea, setNearbyArea] = useState<string>("");
  const [showGpsPrompt, setShowGpsPrompt] = useState(false);
  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const [gpsSuccessAnim, setGpsSuccessAnim] = useState(false);
  const [featuredCharIds, setFeaturedCharIds] = useState<string[]>([]);
  const [activeFeaturedId, setActiveFeaturedId] = useState<string>("");

  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const userMarkerRef = useRef<any>(null);

  const initMap = useCallback(
    (
      chars: Character[],
      center: { lat: number; lng: number },
      grantedGps: boolean,
      featuredId: string = ""
    ) => {
      const newPositions = assignPositions(chars, center, featuredId);
      setPositions(newPositions);

      setTimeout(() => {
        if (!mapRef.current) return;

        if (leafletMapRef.current) {
          leafletMapRef.current.remove();
          leafletMapRef.current = null;
          markersRef.current = [];
          userMarkerRef.current = null;
        }

        const L = (window as any).L;
        if (!L) return;

        const map = L.map(mapRef.current, {
          center: [center.lat, center.lng],
          zoom: DEFAULT_ZOOM,
          zoomControl: true,
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
        }).addTo(map);

        leafletMapRef.current = map;

        if (grantedGps) {
          chars
            .filter((c) => newPositions[c.id])
            .forEach((char) => {
              const pos = newPositions[char.id];
              const imgSrc = char.images[0];
              const isFeatured = char.id === featuredId;

              const icon = L.divIcon({
                className: "",
                html: isFeatured
                  ? `<div style="position:relative;width:64px;height:64px;">
                      <div style="
                        position:absolute;inset:-4px;border-radius:50%;
                        background:linear-gradient(135deg,#f472b6,#fb7185);
                        animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite;
                        opacity:0.5;
                      "></div>
                      <div style="
                        width:64px;height:64px;border-radius:50%;
                        border:3px solid #f472b6;
                        overflow:hidden;background:#fff;cursor:pointer;
                        position:relative;z-index:1;
                        box-shadow:0 0 0 3px rgba(244,114,182,0.4);
                      ">
                        <img src="${imgSrc}" style="width:100%;height:100%;object-fit:cover;object-position:top;" />
                      </div>
                      <div style="
                        position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);
                        background:linear-gradient(135deg,#f472b6,#fb7185);
                        color:white;font-size:9px;font-weight:bold;
                        padding:2px 6px;border-radius:999px;white-space:nowrap;
                        z-index:2;
                      ">★ 人気</div>
                    </div>`
                  : `<div style="
                      width:52px;height:52px;border-radius:50%;
                      border:3px solid #f472b6;
                      overflow:hidden;
                      background:#fff;
                      cursor:pointer;
                    ">
                      <img src="${imgSrc}" style="width:100%;height:100%;object-fit:cover;object-position:top;" />
                    </div>`,
                iconSize: isFeatured ? [64, 72] : [52, 52],
                iconAnchor: isFeatured ? [32, 36] : [26, 26],
              });

              const marker = L.marker([pos.lat, pos.lng], { icon }).addTo(map);
              marker.on("click", () => setSelectedChar(char));
              markersRef.current.push(marker);
            });

          const userIcon = L.divIcon({
            className: "",
            html: `<div style="
              width:20px;height:20px;border-radius:50%;
              background:#f472b6;
              border:3px solid #fff;
              box-shadow:0 0 0 3px rgba(244,114,182,0.4);
            "></div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10],
          });
          userMarkerRef.current = L.marker([center.lat, center.lng], { icon: userIcon })
            .addTo(map)
            .bindPopup("現在地");
        }
      }, 100);
    },
    []
  );

  useEffect(() => {
    const fetchChars = async (): Promise<Character[]> => {
      const { data } = await supabase
        .from("characters")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      return data ? (data as CharacterRow[]).map(rowToCharacter) : [];
    };

    const fetchFeaturedIds = async (): Promise<string[]> => {
      // Try new multi-featured key first
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "featured_char_ids")
        .maybeSingle();
      if (data?.value) {
        try {
          const ids = JSON.parse(data.value);
          if (Array.isArray(ids) && ids.length > 0) return ids;
        } catch { /* ignore */ }
      }
      // Fallback to old single key
      const { data: oldData } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "featured_char_id")
        .maybeSingle();
      return oldData?.value ? [oldData.value] : [];
    };

    // 古いキャッシュを強制クリア（バージョンが違う場合）
    const cachedVer = localStorage.getItem(GPS_CACHE_VERSION_KEY);
    if (cachedVer !== GPS_CACHE_VERSION) {
      localStorage.removeItem(GPS_AREA_KEY);
      localStorage.removeItem(GPS_LOCATION_KEY);
      localStorage.removeItem(GPS_DISPLAYED_IDS_KEY);
      localStorage.setItem(GPS_CACHE_VERSION_KEY, GPS_CACHE_VERSION);
    }

    const savedStatus = localStorage.getItem(GPS_STATUS_KEY);
    const savedArea = localStorage.getItem(GPS_AREA_KEY) ?? "";
    const savedLocRaw = localStorage.getItem(GPS_LOCATION_KEY);
    const savedDisplayedRaw = localStorage.getItem(GPS_DISPLAYED_IDS_KEY);

    Promise.all([fetchChars(), fetchFeaturedIds()]).then(async ([chars, fIds]) => {
      setCharacters(chars);
      setFeaturedCharIds(fIds);

      // Pick seeded featured (30分固定)
      const slot = get30MinSlot();
      const seed = slot * 137;
      const randomFeatured = fIds.length > 0
        ? fIds[Math.floor(seededRandom(seed + 9999) * fIds.length)]
        : "";
      setActiveFeaturedId(randomFeatured);

      const savedDisplayedIds: string[] | null = savedDisplayedRaw
        ? JSON.parse(savedDisplayedRaw)
        : null;

      if (savedStatus === "granted" && savedLocRaw) {
        try {
          const savedLoc = JSON.parse(savedLocRaw) as { lat: number; lng: number };
          // Pick displayed chars (keep previous selection for minor moves)
          const displayed = pickDisplayedChars(chars, fIds, savedDisplayedIds, false);
          setDisplayedChars(displayed);
          localStorage.setItem(GPS_DISPLAYED_IDS_KEY, JSON.stringify(displayed.map((c) => c.id)));

          setUserLocation(savedLoc);
          setNearbyArea(savedArea);
          setGpsStatus("granted");
          setShowGpsPrompt(false);
          initMap(displayed, savedLoc, true, randomFeatured);

          // Silently refresh GPS
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              async (position) => {
                const freshLoc = { lat: position.coords.latitude, lng: position.coords.longitude };
                const latDiff = Math.abs(freshLoc.lat - savedLoc.lat);
                const lngDiff = Math.abs(freshLoc.lng - savedLoc.lng);
                const majorMove = latDiff > LOCATION_MAJOR_THRESHOLD || lngDiff > LOCATION_MAJOR_THRESHOLD;
                const minorMove = latDiff > LOCATION_CHANGE_THRESHOLD || lngDiff > LOCATION_CHANGE_THRESHOLD;

                if (minorMove || majorMove) {
                  setUserLocation(freshLoc);
                  const area = await reverseGeocode(freshLoc.lat, freshLoc.lng);
                  setNearbyArea(area);
                  localStorage.setItem(GPS_AREA_KEY, area);
                  localStorage.setItem(GPS_LOCATION_KEY, JSON.stringify(freshLoc));

                  // Pick seeded featured on major move (30分固定)
                  const newSlot = get30MinSlot();
                  const newSeed = newSlot * 137;
                  const newFeatured = fIds.length > 0
                    ? fIds[Math.floor(seededRandom(newSeed + 9999) * fIds.length)]
                    : "";
                  setActiveFeaturedId(newFeatured);

                  const newDisplayed = pickDisplayedChars(chars, fIds, savedDisplayedIds, majorMove);
                  setDisplayedChars(newDisplayed);
                  localStorage.setItem(GPS_DISPLAYED_IDS_KEY, JSON.stringify(newDisplayed.map((c) => c.id)));
                  initMap(newDisplayed, freshLoc, true, newFeatured);
                }
              },
              () => { /* silent fail */ },
              { timeout: 8000, maximumAge: 0, enableHighAccuracy: true }
            );
          }
          return;
        } catch { /* fall through */ }
      }

      setGpsStatus("loading");
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
            setUserLocation(loc);
            setGpsSuccessAnim(true);
            const area = await reverseGeocode(loc.lat, loc.lng);
            setNearbyArea(area);
            localStorage.setItem(GPS_STATUS_KEY, "granted");
            localStorage.setItem(GPS_AREA_KEY, area);
            localStorage.setItem(GPS_LOCATION_KEY, JSON.stringify(loc));

            const displayed = pickDisplayedChars(chars, fIds, null, true);
            setDisplayedChars(displayed);
            localStorage.setItem(GPS_DISPLAYED_IDS_KEY, JSON.stringify(displayed.map((c) => c.id)));
            initMap(displayed, loc, true, randomFeatured);
            setTimeout(() => {
              setGpsStatus("granted");
              setShowGpsPrompt(false);
              setGpsSuccessAnim(false);
            }, 1800);
          },
          () => {
            setGpsStatus("denied");
            setShowGpsPrompt(true);
            localStorage.setItem(GPS_STATUS_KEY, "denied");
            const displayed = pickDisplayedChars(chars, fIds, null, true);
            setDisplayedChars(displayed);
            initMap(displayed, { lat: 35.6762, lng: 139.6503 }, false, randomFeatured);
          },
          { timeout: 8000, maximumAge: 0 }
        );
      } else {
        setGpsStatus("denied");
        setShowGpsPrompt(true);
        const displayed = pickDisplayedChars(chars, fIds, null, true);
        setDisplayedChars(displayed);
        initMap(displayed, { lat: 35.6762, lng: 139.6503 }, false, randomFeatured);
      }
    });

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        markersRef.current = [];
        userMarkerRef.current = null;
      }
    };
  }, [initMap]);

  const getDeviceGuidance = () => {
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    if (isIOS) {
      return {
        title: "位置情報の許可が必要です",
        steps: [
          "「設定」アプリを開く",
          "「プライバシーとセキュリティ」→「位置情報サービス」",
          "ブラウザアプリ（Safari等）を選択",
          "「このAppの使用中」または「常に」を選択",
        ],
        buttonLabel: "許可後にページを再読み込み",
      };
    }
    if (isAndroid) {
      return {
        title: "位置情報の許可が必要です",
        steps: [
          "「設定」アプリを開く",
          "「アプリ」→ブラウザアプリ（Chrome等）を選択",
          "「権限」→「位置情報」を選択",
          "「アプリの使用中のみ許可」を選択",
        ],
        buttonLabel: "許可後にページを再読み込み",
      };
    }
    return {
      title: "ブラウザで位置情報がブロックされています",
      steps: [
        "アドレスバー横の「🔒」または「ⓘ」アイコンをクリック",
        "「位置情報」を「許可」に変更",
        "ページを再読み込みしてください",
      ],
      buttonLabel: "許可後にページを再読み込み",
    };
  };

  const doGetPosition = useCallback(() => {
    setGpsStatus("loading");
    setPermissionBlocked(false);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
        setUserLocation(loc);
        setGpsSuccessAnim(true);
        const area = await reverseGeocode(loc.lat, loc.lng);
        setNearbyArea(area);
        localStorage.setItem(GPS_STATUS_KEY, "granted");
        localStorage.setItem(GPS_AREA_KEY, area);
        localStorage.setItem(GPS_LOCATION_KEY, JSON.stringify(loc));

        const { data: freshData } = await supabase
          .from("characters")
          .select("*")
          .eq("is_active", true)
          .order("sort_order", { ascending: true });
        const chars = freshData ? (freshData as CharacterRow[]).map(rowToCharacter) : [];
        setCharacters(chars);

        const doSlot = get30MinSlot();
        const doSeed = doSlot * 137;
        const newFeatured = featuredCharIds.length > 0
          ? featuredCharIds[Math.floor(seededRandom(doSeed + 9999) * featuredCharIds.length)]
          : "";
        setActiveFeaturedId(newFeatured);

        const displayed = pickDisplayedChars(chars, featuredCharIds, null, true);
        setDisplayedChars(displayed);
        localStorage.setItem(GPS_DISPLAYED_IDS_KEY, JSON.stringify(displayed.map((c) => c.id)));
        initMap(displayed, loc, true, newFeatured);

        setTimeout(() => {
          setGpsStatus("granted");
          setShowGpsPrompt(false);
          setGpsSuccessAnim(false);
        }, 1800);
      },
      (err) => {
        setGpsStatus("denied");
        setShowGpsPrompt(true);
        if (err.code === err.PERMISSION_DENIED) {
          setPermissionBlocked(true);
        }
      },
      { timeout: 10000, maximumAge: 0, enableHighAccuracy: true }
    );
  }, [initMap, featuredCharIds]);

  const handleRequestGps = useCallback(async () => {
    if (!navigator.geolocation) return;

    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({ name: "geolocation" });
        if (result.state === "denied") {
          setPermissionBlocked(true);
          setGpsStatus("denied");
          setShowGpsPrompt(true);
          return;
        }
        doGetPosition();
        return;
      } catch { /* ignore */ }
    }
    doGetPosition();
  }, [doGetPosition]);

  useEffect(() => {
    if (showGpsPrompt && gpsStatus === "denied") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showGpsPrompt, gpsStatus]);

  const sortedChars = [...displayedChars].sort((a, b) => {
    if (activeTab === "age") {
      // null (ひみつ) は最後に
      if (a.age == null && b.age == null) return 0;
      if (a.age == null) return 1;
      if (b.age == null) return -1;
      return a.age - b.age;
    }
    // distance: featured first, then original order
    if (a.id === activeFeaturedId) return -1;
    if (b.id === activeFeaturedId) return 1;
    return 0;
  });

  const isGpsPromptVisible = showGpsPrompt && gpsStatus === "denied";

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito', sans-serif" }}>
      {isGpsPromptVisible && (
        <div
          className="fixed inset-0 z-[900] bg-black/50 backdrop-blur-[2px]"
          style={{ pointerEvents: "none" }}
        />
      )}
      <Navbar />

      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" />

      <div className="pt-16">
        {/* Map Header */}
        <div className="bg-gradient-to-r from-pink-50 to-rose-50 border-b border-pink-100 px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-pink-100 rounded-full">
              <i className="ri-map-pin-2-fill text-pink-500 text-sm"></i>
            </div>
            {gpsStatus === "granted" ? (
              <span className="text-sm font-semibold text-gray-700">
                {displayedChars.length}人の女の子があなたの近くにいます
              </span>
            ) : gpsStatus === "denied" ? (
              <span className="text-sm font-semibold text-gray-500">
                GPSを許可すると近くの女の子が見つかります
              </span>
            ) : (
              <span className="text-sm font-semibold text-gray-500">GPS取得中...</span>
            )}

            {gpsStatus === "loading" && (
              <span className="text-xs text-pink-400 flex items-center gap-1">
                <i className="ri-loader-4-line animate-spin"></i>GPS取得中...
              </span>
            )}
            {gpsStatus === "granted" && (
              <span className="text-xs text-green-500 flex items-center gap-1">
                <i className="ri-crosshair-2-line"></i>
                {nearbyArea ? `${nearbyArea}周辺を表示中` : "現在地周辺を表示中"}
              </span>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="relative" style={{ height: "clamp(280px, 50vw, 480px)" }}>
          <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

          {/* GPS Denied Overlay */}
          {gpsStatus === "denied" && showGpsPrompt && (
            <div className="absolute inset-0 z-[1000] flex items-end sm:items-center justify-center bg-gradient-to-b from-black/40 to-black/60 backdrop-blur-sm">
              <div className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:mx-4 sm:max-w-sm text-center overflow-hidden max-h-[90svh] overflow-y-auto">
                <div className="bg-gradient-to-r from-pink-400 to-rose-400 px-5 pt-5 pb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
                    <i className="ri-map-pin-2-fill text-white text-2xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-white mb-1 leading-snug">
                    近くにHな女の子がいるかも♡
                  </h3>
                  <p className="text-pink-100 text-xs leading-relaxed">
                    あなたの現在地を使って、<br />すぐ近くの女の子を発見しよう！
                  </p>
                </div>

                <div className="px-5 py-4">
                  <div className="flex flex-col gap-2 mb-4">
                    {[
                      { icon: "ri-map-pin-line", text: "現在地から最も近い女の子を表示" },
                      { icon: "ri-shield-check-line", text: "位置情報は外部に送信されません" },
                      { icon: "ri-time-line", text: "女の子の位置は15分ごとに更新" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 text-left">
                        <div className="w-8 h-8 flex items-center justify-center bg-pink-50 rounded-full flex-shrink-0">
                          <i className={`${item.icon} text-pink-400 text-sm`}></i>
                        </div>
                        <p className="text-sm text-gray-600">{item.text}</p>
                      </div>
                    ))}
                  </div>

                  {permissionBlocked ? (
                    <div className="w-full">
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-3 text-left">
                        <div className="flex items-start gap-2">
                          <i className="ri-error-warning-line text-amber-500 text-base mt-0.5 flex-shrink-0"></i>
                          <div>
                            <p className="text-xs font-bold text-amber-700 mb-2">{getDeviceGuidance().title}</p>
                            <ol className="list-none space-y-1">
                              {getDeviceGuidance().steps.map((step, i) => (
                                <li key={i} className="text-xs text-amber-600 leading-relaxed flex items-start gap-1.5">
                                  <span className="font-bold text-amber-500 flex-shrink-0">{i + 1}.</span>
                                  <span>{step}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => window.location.reload()}
                        className="w-full py-4 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-full font-bold text-sm hover:from-amber-500 hover:to-orange-500 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-refresh-line mr-2"></i>{getDeviceGuidance().buttonLabel}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleRequestGps}
                      className="w-full py-4 bg-gradient-to-r from-pink-400 to-rose-400 text-white rounded-full font-bold text-sm hover:from-pink-500 hover:to-rose-500 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-crosshair-2-line mr-2"></i>現在地を許可して女の子を探す♡
                    </button>
                  )}
                  <p className="text-xs text-gray-400 mt-3 leading-relaxed">
                    位置情報は外部に送信されませんのでご安心下さい。
                  </p>
                  <button
                    onClick={() => setShowGpsPrompt(false)}
                    className="mt-3 text-xs text-gray-400 hover:text-gray-600 cursor-pointer underline"
                  >
                    今はしない
                  </button>
                  <div className="mt-3 flex flex-col items-center w-full">
                    <div className="relative bg-emerald-500 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-1.5 whitespace-nowrap">
                      とりあえず！
                      <span
                        className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0"
                        style={{
                          borderLeft: "7px solid transparent",
                          borderRight: "7px solid transparent",
                          borderTop: "8px solid #10b981",
                        }}
                      />
                    </div>
                    <Link
                      to="/characters"
                      className="w-full py-3.5 bg-gradient-to-r from-emerald-400 to-green-500 text-white rounded-full font-bold text-sm hover:from-emerald-500 hover:to-green-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-heart-line"></i>今すぐ会える女の子一覧から探す♡
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* GPS Success Animation */}
          {gpsSuccessAnim && (
            <div className="absolute inset-0 z-[1001] flex items-center justify-center bg-white/80 backdrop-blur-sm transition-all duration-700">
              <div className="flex flex-col items-center gap-4">
                <div className="relative w-20 h-20 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-green-400/30 animate-ping" style={{ animationDuration: "1s" }} />
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-emerald-400 to-green-500 rounded-full">
                    <i className="ri-map-pin-2-fill text-white text-3xl"></i>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-lg font-black text-gray-800">現在地を取得しました！</p>
                  <p className="text-sm text-emerald-500 font-medium mt-1">近くの女の子を表示しています♡</p>
                </div>
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-2 h-2 rounded-full bg-pink-400 animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* GPS Loading */}
          {gpsStatus === "loading" && (
            <div className="absolute inset-0 z-[999] flex items-center justify-center bg-white/60 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-pink-100 rounded-full">
                  <i className="ri-loader-4-line text-pink-500 text-xl animate-spin"></i>
                </div>
                <p className="text-sm text-gray-600 font-medium">現在地を取得中...</p>
              </div>
            </div>
          )}

          {/* Selected Character Popup */}
          {selectedChar && gpsStatus === "granted" && (
            <div className="absolute bottom-3 left-3 right-3 md:left-6 md:right-auto md:w-80 bg-white rounded-2xl border border-pink-100 p-3 md:p-4 z-[1000] animate-fade-in">
              <button
                className="absolute top-2.5 right-2.5 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                onClick={() => setSelectedChar(null)}
              >
                <i className="ri-close-line"></i>
              </button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 md:w-14 md:h-16 rounded-full overflow-hidden border-2 border-pink-300 flex-shrink-0">
                  <img
                    src={selectedChar.images[0]}
                    alt={selectedChar.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 md:gap-2 mb-0.5 flex-wrap">
                    <h3 className="font-bold text-gray-800 text-sm md:text-base">{selectedChar.name}</h3>
                    <span className="text-pink-400 text-xs md:text-sm font-medium">{selectedChar.age != null ? `${selectedChar.age}歳` : "ひみつ"}</span>
                    <span className="bg-green-100 text-green-600 text-xs px-1.5 py-0.5 rounded-full">オンライン</span>
                  </div>
                  {gpsStatus === "granted" ? (
                    <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                      <i className="ri-map-pin-line text-pink-400"></i>
                      {nearbyArea || "現在地周辺"}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mb-1">
                      <i className="ri-map-pin-line"></i>
                      <span className="italic">GPSを許可すると所在地が表示されます</span>
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {selectedChar.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-xs bg-pink-50 text-pink-400 px-2 py-0.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-2.5 flex flex-col gap-2">
                <a
                  href={selectedChar.link}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="w-full py-2.5 text-center rounded-full text-xs font-black text-white transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                  style={{
                    background: "linear-gradient(135deg, #f43f5e 0%, #fb7185 50%, #f43f5e 100%)",
                    boxShadow: "0 4px 16px rgba(244,63,94,0.5)",
                    animation: "pulse-glow 2s ease-in-out infinite",
                  }}
                >
                  <i className="ri-heart-fill text-sm"></i>
                  会いに行く♡
                  <i className="ri-arrow-right-line text-sm"></i>
                </a>
                <Link
                  to={`/profile/${selectedChar.id}`}
                  className="w-full py-2 bg-pink-50 text-pink-500 rounded-full text-xs font-semibold text-center hover:bg-pink-100 transition-all cursor-pointer border border-pink-200"
                >
                  プロフィールを見る
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* GPS Denied inline */}
        {gpsStatus === "denied" && !showGpsPrompt && (
          <div className={`px-4 md:px-6 py-3 md:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${permissionBlocked ? "bg-gradient-to-r from-amber-400 to-orange-400" : "bg-gradient-to-r from-pink-400 to-rose-400"}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                <i className={`${permissionBlocked ? "ri-error-warning-line" : "ri-map-pin-2-fill"} text-white text-base`}></i>
              </div>
              <div>
                {permissionBlocked ? (
                  <>
                    <p className="text-sm font-bold text-white">{getDeviceGuidance().title}</p>
                    <p className="text-xs text-amber-100">設定から位置情報を許可してページを再読み込みしてください</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-bold text-white">GPSを許可すると近くにいるHな女の子がわかります♡</p>
                    <p className="text-xs text-pink-100">位置情報は外部に送信されませんのでご安心下さい</p>
                  </>
                )}
              </div>
            </div>
            {permissionBlocked ? (
              <button
                onClick={() => window.location.reload()}
                className="w-full sm:w-auto px-6 py-2.5 bg-white text-amber-500 rounded-full text-xs font-bold hover:bg-amber-50 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-refresh-line mr-1"></i>許可後に再読み込み
              </button>
            ) : (
              <button
                onClick={handleRequestGps}
                className="w-full sm:w-auto px-6 py-2.5 bg-white text-pink-500 rounded-full text-xs font-bold hover:bg-pink-50 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-crosshair-2-line mr-1"></i>現在地を許可して探す♡
              </button>
            )}
          </div>
        )}

        {/* Character List */}
        <div className={`max-w-6xl mx-auto px-3 md:px-6 py-5 md:py-8 ${isGpsPromptVisible ? "invisible pointer-events-none" : ""}`}>
          <div className="flex gap-1 md:gap-2 mb-5 md:mb-6 bg-pink-50 p-1 rounded-full w-fit">
            {[
              { key: "distance", label: "距離順", icon: "ri-map-pin-line" },
              { key: "age", label: "年齢順", icon: "ri-bar-chart-line" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as "distance" | "age")}
                className={`flex items-center gap-1 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? "bg-pink-500 text-white"
                    : "text-gray-500 hover:text-pink-500"
                }`}
              >
                <i className={tab.icon}></i>{tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2.5 md:gap-3">
            {sortedChars.map((char) => (
              <div
                key={char.id}
                className="flex items-center gap-3 md:gap-4 bg-white border border-pink-100 rounded-2xl p-3 md:p-4 hover:border-pink-300 transition-all"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden border-2 border-pink-200 flex-shrink-0">
                  <img
                    src={char.images[0]}
                    alt={char.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 md:gap-2 mb-0.5 flex-wrap">
                    <h3 className="font-bold text-gray-800 text-sm md:text-base">{char.name}</h3>
                    <span className="text-pink-400 text-xs md:text-sm font-medium">{char.age != null ? `${char.age}歳` : "ひみつ"}</span>
                    {char.id === activeFeaturedId && (
                      <span className="text-xs bg-pink-100 text-pink-500 px-1.5 py-0.5 rounded-full font-semibold">★ 人気</span>
                    )}
                    <span className="bg-green-100 text-green-600 text-xs px-1.5 py-0.5 rounded-full">オンライン</span>
                  </div>
                  {gpsStatus === "granted" ? (
                    <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                      <i className="ri-map-pin-line text-pink-400"></i>
                      {nearbyArea || "現在地周辺"}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mb-1">
                      <i className="ri-map-pin-line"></i>
                      <span className="italic">GPSを許可すると所在地が表示されます</span>
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {char.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-xs bg-pink-50 text-pink-400 px-2 py-0.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 md:gap-2 flex-shrink-0">
                  <a
                    href={char.link}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="px-3 md:px-5 py-2.5 rounded-full text-xs font-black text-white whitespace-nowrap cursor-pointer text-center flex items-center justify-center gap-1 transition-all"
                    style={{
                      background: "linear-gradient(135deg, #f43f5e 0%, #fb7185 50%, #f43f5e 100%)",
                      boxShadow: "0 3px 12px rgba(244,63,94,0.5)",
                    }}
                  >
                    <i className="ri-heart-fill"></i>
                    会いに行く♡
                  </a>
                  <Link
                    to={`/profile/${char.id}`}
                    className="px-3 md:px-4 py-2 bg-pink-50 text-pink-500 rounded-full text-xs font-semibold hover:bg-pink-100 transition-all whitespace-nowrap cursor-pointer text-center border border-pink-200"
                  >
                    プロフィール
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MapPage;
